'use client'

import Link from 'next/link'
import { Fragment, useEffect, useRef, useState } from 'react'

import { NavigationLinkDTO } from '@/lib/models/model-types/navigation/navigation.types'
import { Icon } from '@/lib/ui-kit/components/ui/Icon.component'
import { IconType } from '@/lib/ui-kit/components/ui/Icon.types'
import { cn } from '@/lib/utils'

import { Logo } from './ui/Logo'

interface MobileNavHeaderProps {
  className?: string
  links: NavigationLinkDTO[]
  children?: React.ReactNode
}

export const NavHeaderMobile = ({
  className,
  links,
  children,
}: MobileNavHeaderProps) => {
  // Add the scroll behavior logic directly here
  const lastScrollRef = useRef(0)
  const [isScrollingUp, setIsScrollingUp] = useState(true)
  const [, setIsAtTop] = useState(true)

  useEffect(() => {
    const handleScroll = () => {
      // clamping the value to be always positive, on Apple devices it can be negative
      // due to elastic scroll
      const scrollTop = Math.max(
        0,
        window.pageYOffset || document.documentElement.scrollTop
      )

      setIsScrollingUp(scrollTop <= lastScrollRef.current)
      setIsAtTop(scrollTop === 0)
      lastScrollRef.current = scrollTop
    }

    handleScroll() // Initial call

    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const [isActive, setIsActive] = useState(false)
  const [activeItem, setActiveItem] = useState('')

  useEffect(() => {
    if (isActive) {
      document.body.classList.add('overflow-hidden')
    } else {
      document.body.classList.remove('overflow-hidden')
    }
  }, [isActive])

  const onItemClick = (title: string) => {
    setActiveItem(activeItem === title ? '' : title)
  }

  const close = () => {
    setIsActive(false)
  }

  const toggle = () => {
    setIsActive(!isActive)
    setActiveItem('')
  }

  return (
    <header
      className={cn(
        className,
        {
          'scroll-up': !isScrollingUp,
        },
        `fixed z-30 !max-h-[100dvh] max-h-[100vh] w-[100vw] flex-col items-start justify-start bg-gray-200 text-left text-gray-1000 transition-all duration-500 ease-in-out [backdrop-filter:blur(80px)]`
      )}
    >
      <div className="box-border w-full px-6 py-4">
        <section className="flex w-full flex-row items-center justify-between">
          <Link href="/" className="h-[48px] w-[175px]">
            <Logo />
          </Link>
          <div className="flex">
            {!isActive && children}
            <button
              className={`flex flex-col items-start justify-start rounded-full ${
                isActive && 'bg-gray-50'
              }`}
              onClick={toggle}
            >
              <div className="flex flex-row items-center justify-start p-4">
                <div className="flex flex-row items-center justify-start">
                  <Icon
                    type={isActive ? IconType.close : IconType.navigation}
                  />
                </div>
              </div>
            </button>
          </div>
        </section>
      </div>

      <div
        className={`${
          isActive ? 'flex' : 'hidden'
        } flex-col items-center justify-start gap-[40px] self-stretch overflow-auto px-0 pt-0`}
      >
        <div className="flex w-full flex-col items-start justify-start">
          {links.map(({ link, label, subMenu }, index) => (
            <div
              className={`flex w-full flex-col items-end justify-start border-b-[1px] border-solid border-gray-transparent-1000 px-0 first:pt-4 ${
                activeItem === label && 'bg-gray-50'
              }`}
              key={index}
            >
              {subMenu.length === 0 && (
                <Link
                  href={link}
                  onClick={close}
                  className="box-border flex w-full cursor-pointer flex-row items-center justify-start px-0 py-6 text-left"
                >
                  <button onClick={() => onItemClick(label)}>
                    <div className="flex flex-1 flex-row items-center justify-between px-6 py-0">
                      <div className="paragraph-xl flex-1">{label}</div>
                    </div>
                  </button>
                </Link>
              )}

              {subMenu.length > 0 && (
                <>
                  <button
                    ref={(el) => {
                      if (activeItem === label) {
                        el?.scrollIntoView({ behavior: 'smooth' })
                      }
                    }}
                    value={label}
                    onClick={() => onItemClick(label)}
                    className="box-border flex w-full cursor-pointer flex-row items-center justify-start px-0 py-6 text-left"
                  >
                    <div className="flex flex-1 flex-row items-center justify-between px-6 py-0">
                      <Link href={link}>
                        <div className="paragraph-xl flex-1">{label}</div>
                      </Link>
                      <Icon type={IconType.arrow_down} />
                    </div>
                  </button>

                  {subMenu.map(({ title, subLinks }, index) => {
                    return (
                      <Fragment key={`${title || ''}-${index}`}>
                        {title && (
                          <div
                            className={`paragraph-xxl self-start px-6 font-semibold text-gray-1000 ${
                              activeItem === label ? 'flex' : 'hidden'
                            } ${index === 0 ? 'pt-0' : 'pt-6'}`}
                          >
                            {title}
                          </div>
                        )}
                        {subLinks.map(
                          (
                            { label: subLinkLabel, link, description },
                            index
                          ) => (
                            <Link
                              key={index}
                              className={`box-border min-h-[74px] w-full flex-row items-center justify-start border-b-[1px] border-solid border-gray-transparent-1000 px-6 py-4 last:border-b-0 ${
                                activeItem === label ? 'flex' : 'hidden'
                              }`}
                              href={link}
                              onClick={close}
                            >
                              <div className="flex flex-1 flex-col items-start justify-start gap-[8px]">
                                <div className="paragraph-l">
                                  {subLinkLabel}
                                </div>
                                {description ? (
                                  <div className="paragraph-xs text-[13px] leading-[20px] text-gray-800">
                                    {description}
                                  </div>
                                ) : null}
                              </div>
                            </Link>
                          )
                        )}
                      </Fragment>
                    )
                  })}
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </header>
  )
}
